import * as React from 'react';

import { compose } from 'recompose';

import { useLocation } from 'js/lib/useRouter';
import user, { isAuthenticatedUser } from 'js/lib/user';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { allContractsHardPausedOrTerminated } from 'bundles/enterprise-admin-contracts/enterprise-admin-contracts-index';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import ProgramArchivedDenyModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramArchivedDenyModal';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import { InternalServerError, NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useEnterpriseContracts,
  useFailedToFetchProgram,
  useIsProgramLoading,
  useProgram,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useAutoenrollmentStatus } from 'bundles/unified-common/hooks/useAutoenrollmentStatus';
import { useSsoLockedOutStatus } from 'bundles/unified-common/hooks/useSsoLockedOutStatus';

type Props = {
  naptime?: InjectedNaptime;
  children: React.ReactNode;
};

export const EnterprisePageGuard = ({ children, naptime }: Props) => {
  const program = useProgram();
  const failedToFetchProgram = useFailedToFetchProgram();
  const enterpriseContracts = useEnterpriseContracts();
  const isProgramLoading = useIsProgramLoading();
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const thirdPartyOrg = useThirdPartyOrganization();
  const { isLmsLxpAutoEnrollmentEnabled } = useAutoenrollmentStatus();
  const { isSsoLockedOut: isSsoLockedOutAdminSetting, isLoading: isSsoLockedAdminSettingLoading } =
    useSsoLockedOutStatus();

  if (isSsoLockedAdminSettingLoading) {
    return;
  }

  const isLearnerPartOfLfoEnabledOrg =
    (features?.enableOptimizedLearnerFlow || isLmsLxpAutoEnrollmentEnabled) && location.query.isExternal === 'true';
  const isOrgWhiteListedForSSOLogin =
    features?.whitelistOrganizationForSSOLogin || features?.whitelistOrganizationProgramsForSSOLogin;
  const isSsoLockedOutEnterprise = features?.enableUnifiedSso
    ? isSsoLockedOutAdminSetting
    : features?.enableForceSsoLogin || features?.enableForceSsoLoginV2Beta;
  const shouldRedirectToIdpOnOrg =
    isLearnerPartOfLfoEnabledOrg && isSsoLockedOutEnterprise && isOrgWhiteListedForSSOLogin;
  const shouldRedirectToIdp =
    location.query.attemptSSOLogin === 'true' || (!isAuthenticatedUser() && shouldRedirectToIdpOnOrg);

  if (shouldRedirectToIdp && thirdPartyOrg && thirdPartyOrg.loginMethod === 'SAML') {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrg.id} />;
  }

  const shouldRedirectToLoginForLfo =
    !isAuthenticatedUser() &&
    (features?.enableOptimizedLearnerFlow || isLmsLxpAutoEnrollmentEnabled) &&
    location.query.isExternal &&
    !location.query.attemptSSOLogin;

  if (shouldRedirectToLoginForLfo) {
    redirectClientOrServer('/login?redirectTo=' + encodeURIComponent(location.pathname + location.search));
    return null;
  }

  // Show program archived page if program is archived.
  if (
    !isProgramLoading &&
    program &&
    (program?.endedAt || (enterpriseContracts && allContractsHardPausedOrTerminated(enterpriseContracts)))
  ) {
    return <ProgramArchivedDenyModal userId={user.get()?.id} naptime={naptime} />;
  }

  // If no program found, render NotFound page.
  if (!isProgramLoading && !program && !failedToFetchProgram) {
    if (inServerContext) {
      throw new NotFoundError();
    } else {
      return <NotFoundPage />;
    }
  } else if (!isProgramLoading && !program && failedToFetchProgram) {
    if (inServerContext) {
      throw new InternalServerError('Failed to fetch program.');
    } else {
      // We don't have a client-side error page, show not found page for now.
      return <NotFoundPage />;
    }
  }

  return <React.Fragment>{children}</React.Fragment>;
};

/**
 * @description Checks data required to serve an enterprise page, if said data is not present returns appropriate error page.
 */
export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterprisePageGuard);
